import React from 'react';
import { Button, InputText, InputTextArea, Modal } from '@companycam/slab-web';
import { type SubmitHandler, useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import styled from 'styled-components';
import { type Editor } from '@companycam/tiptap-editor';

import { translate } from 'javascripts/translations/i18n';
import { MINIMUM_CHARGE_IN_CENTS } from '../constants';
import { stripFormatting, formatInputToCurrency } from '../helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreatePaymentRequest } from '../hooks/useCreatePaymentRequest';
import { type PaymentRequest } from '../types';
import TransactionLimitFormLink from './TransactionLimitFormLink';

const InputRow = styled.div`
  width: 100%;
  margin-bottom: var(--cc_size_spacing_m);
`;

const TransactionLimitWrapper = styled.div<{ $doubleMargin?: boolean }>`
  text-align: right;
  margin-top: ${(props) => (props.$doubleMargin ? '-20px' : '0')};
`;

const generatePaymentRequestSchema = (maxAmountInCents: number) => {
  const maxAmountInDollars = formatInputToCurrency(maxAmountInCents / 100);

  const limitErrorMessage = `${translate(
    'paymentRequest.modal.totalRequired',
  )} ${maxAmountInDollars}`;

  return Yup.object().shape({
    title: Yup.string().required(
      translate('paymentRequest.modal.titleRequired'),
    ),
    total: Yup.number()
      .transform((value, originalValue, context) => {
        if (context.isType(value)) return value;

        const newValue = stripFormatting(originalValue);

        return context.isType(newValue) ? newValue : 0.0;
      })
      .min(MINIMUM_CHARGE_IN_CENTS / 100, limitErrorMessage)
      .max(maxAmountInCents / 100, limitErrorMessage)
      .required(limitErrorMessage),
    memo: Yup.string().nullable(),
  });
};

type PaymentRequestFormInputs = {
  title: string;
  total: string;
  memo: string;
  status: 'draft' | 'shared';
};

export type PaymentRequestModalV2Props = {
  onRequestClose: () => void;
  projectId: number;
  editor: Editor;
  maxAmountInCents: number;
};

const PaymentRequestModalV2 = ({
  onRequestClose,
  projectId,
  editor,
  maxAmountInCents,
}: PaymentRequestModalV2Props) => {
  const schema = generatePaymentRequestSchema(maxAmountInCents);
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<PaymentRequestFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      total: '',
      memo: '',
    },
  });

  const insertLink = (url: string) => {
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ class: 'payment-link-cc', href: url })
      .command(({ tr: transaction }) => {
        transaction.insertText(translate('common.payNow'));
        return true;
      })
      .toggleMark('link')
      .run();
  };

  const postSubmit = (res: PaymentRequest) => {
    insertLink(res.trusty_checkout_session_link);
    onRequestClose();
  };

  const { createPaymentRequest } = useCreatePaymentRequest({
    projectDatabaseId: projectId,
    onSuccess: postSubmit,
    eventSource: 'pages',
  });

  const onSubmit: SubmitHandler<PaymentRequestFormInputs> = (data) => {
    createPaymentRequest({
      title: data.title,
      amount: data.total.toString(),
      memo: data.memo,
      status: 'shared',
    });
  };

  return (
    <Modal
      title={translate('payments.header.create')}
      onClose={onRequestClose}
      isOpen
      footerActions={
        <Button type="submit" color="primary" onClick={handleSubmit(onSubmit)}>
          {translate('common.submit')}
        </Button>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputRow>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <InputText
                status={errors.title ? 'error' : 'normal'}
                message={errors.title?.message}
                placeholder={translate('common.title')}
                id="title"
                label="Title"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
              />
            )}
          />
        </InputRow>
        <InputRow>
          <Controller
            name="total"
            control={control}
            render={({ field }) => (
              <InputText
                status={errors.total ? 'error' : 'normal'}
                message={errors.total?.message}
                id="total"
                label="Total"
                placeholder={translate('paymentRequest.modal.totalPlaceholder')}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                onBlur={() => {
                  const formattedInput = formatInputToCurrency(field.value);
                  setValue('total', formattedInput);
                }}
              />
            )}
          />
          <TransactionLimitWrapper $doubleMargin={!!errors.total}>
            <TransactionLimitFormLink
              href="https://forms.reform.app/trusty/transaction-limit-request/9spx03"
              target="_blank"
              rel="noreferrer"
              $doubleMargin={false}
            >
              {translate('paymentRequest.modal.requestHigherLimit')}
            </TransactionLimitFormLink>
          </TransactionLimitWrapper>
        </InputRow>

        <InputRow>
          <Controller
            name="memo"
            control={control}
            render={({ field }) => (
              <InputTextArea
                id="memo"
                label="Memo"
                placeholder={translate('paymentRequest.modal.memoPlaceholder')}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
              />
            )}
          />
        </InputRow>
      </form>
    </Modal>
  );
};

export default PaymentRequestModalV2;
